/**
 * The twelve tabs of the domain page (owner's v2.1 spec, docs/design/registrar-ops-console-v2.1-domain-detail.html)
 * and where every former URL went: the 17 tabs before 2026-09-29 and the six tabs with views
 * (`?view=`) after it. A former page that is now one part of a tab lands on that part (`#section`).
 * Plain data so node tests can load it.
 */

/** In the order of the tab grid. `overview` is the bare domain URL. Labels: `domainPage:tabs.*`. */
export const DOMAIN_TAB_IDS = ['overview', 'subject', 'dossier', 'dns', 'dnssec', 'addons', 'finance', 'lifecycle', 'transfer', 'registry', 'compliance', 'history'] as const;

export type DomainTabId = (typeof DOMAIN_TAB_IDS)[number];

export interface Place {
  tab: DomainTabId;
  /** Anchor of the part of the tab that shows the former page. */
  section?: string;
}

/** Former tab segments that are not tabs any more. */
export const MOVED_TABS: Readonly<Record<string, Place>> = {
  contacts: { tab: 'subject', section: 'contacts' },
  documents: { tab: 'dossier', section: 'documents' },
  nameservers: { tab: 'dns' },
  vnnic: { tab: 'compliance', section: 'vnnic' },
  epp: { tab: 'registry', section: 'commands' },
  observability: { tab: 'registry', section: 'monitoring' },
  transfers: { tab: 'transfer', section: 'transfers' },
  audit: { tab: 'history', section: 'audit' },
  orders: { tab: 'finance', section: 'orders' },
  invoices: { tab: 'finance', section: 'invoices' },
  services: { tab: 'addons' },
  // The six-tab page's "Dịch vụ & thanh toán" opened on its add-ons.
  billing: { tab: 'addons' },
};

/** The six-tab page's views, `/<tab>?view=<view>`, keyed `<tab>:<view>`. */
export const MOVED_VIEWS: Readonly<Record<string, Place>> = {
  'dossier:dossier': { tab: 'dossier' },
  'dossier:compliance': { tab: 'compliance' },
  'dossier:vnnic': { tab: 'compliance', section: 'vnnic' },
  'registry:registry': { tab: 'registry' },
  'registry:history': { tab: 'history' },
  'registry:transfers': { tab: 'transfer', section: 'transfers' },
  'registry:audit': { tab: 'history', section: 'audit' },
  'registry:observability': { tab: 'registry', section: 'monitoring' },
  'billing:services': { tab: 'addons' },
  'billing:privacy': { tab: 'addons', section: 'privacy' },
  'billing:orders': { tab: 'finance', section: 'orders' },
  'billing:invoices': { tab: 'finance', section: 'invoices' },
  'billing:billing': { tab: 'finance' },
};

export const isDomainTab = (segment: string): segment is DomainTabId => (DOMAIN_TAB_IDS as readonly string[]).includes(segment);

/** Where a URL segment (and a former `?view=`) is shown now; null for an unknown segment. */
export function placeFor(segment: string, view?: string | null): Place | null {
  const viewed = view ? MOVED_VIEWS[`${segment}:${view}`] : undefined;
  if (viewed) return viewed;
  const moved = MOVED_TABS[segment];
  if (moved) return moved;
  return isDomainTab(segment) ? { tab: segment } : null;
}

/** `/domains/<name>/<tab>?action=…#section` (the overview is the bare domain URL). */
export function domainTabUrl(asciiName: string, tab: DomainTabId, section?: string, action?: string): string {
  const base = `/domains/${encodeURIComponent(asciiName)}${tab === 'overview' ? '' : `/${tab}`}`;
  return `${base}${action ? `?action=${encodeURIComponent(action)}` : ''}${section ? `#${section}` : ''}`;
}
